import { useEffect, useRef, useState } from 'react'
import { ResponsiveContainer } from 'recharts'
import './chart.css'

function isRenderComponent(comp) {
  return typeof comp === 'function' || (typeof comp === 'object' && comp !== null)
}

function isDashboardReportCaptureMode() {
  return typeof document !== 'undefined' && document.body.classList.contains('dashboard-report-capture-mode')
}

const ChartContainer = ({
  height = 240,
  loading = false,
  empty = false,
  emptyMessage = 'No data available',
  className = '',
  forceRender = false,
  children,
}) => {
  const containerRef = useRef(null)
  const [hasEnteredView, setHasEnteredView] = useState(
    () => forceRender || isDashboardReportCaptureMode(),
  )
  const [animateIn, setAnimateIn] = useState(false)

  useEffect(() => {
    if (forceRender || hasEnteredView) return undefined
    const syncCaptureMode = () => {
      if (isDashboardReportCaptureMode()) setHasEnteredView(true)
    }
    syncCaptureMode()
    if (typeof MutationObserver === 'undefined') return undefined
    const observer = new MutationObserver(syncCaptureMode)
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [forceRender, hasEnteredView])

  useEffect(() => {
    if (forceRender || hasEnteredView) return
    const node = containerRef.current
    if (!node) return
    if (typeof IntersectionObserver === 'undefined') {
      setHasEnteredView(true)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasEnteredView(true)
          observer.disconnect()
        }
      },
      { root: null, threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [hasEnteredView, forceRender])

  useEffect(() => {
    if (!hasEnteredView) return
    const rafId = requestAnimationFrame(() => setAnimateIn(true))
    return () => cancelAnimationFrame(rafId)
  }, [hasEnteredView])

  if (!hasEnteredView) {
    return (
      <div
        ref={containerRef}
        className={`rg-chart rg-chart--pending ${className}`}
        style={{ height }}
        aria-hidden="true"
      />
    )
  }

  if (loading) {
    return (
      <div ref={containerRef} className={`rg-chart__loading ${className}`}>
        Loading chart...
      </div>
    )
  }

  if (empty) {
    return (
      <div
        ref={containerRef}
        className={`rg-chart__empty ${className}`}
        style={{ height }}
        role="status"
      >
        {emptyMessage}
      </div>
    )
  }

  const useFullHeight = height === '100%' || height === '100'

  const pixelFallback =
    typeof height === 'number' && Number.isFinite(height)
      ? Math.max(120, Math.round(height))
      : 240
  const minChart = useFullHeight ? 180 : pixelFallback
  const initialW = 480
  const initialH = useFullHeight ? Math.max(minChart, 240) : pixelFallback

  const chartBody = isRenderComponent(ResponsiveContainer) ? (
    <ResponsiveContainer
      width="100%"
      height="100%"
      minWidth={0}
      minHeight={minChart}
      initialDimension={{ width: initialW, height: initialH }}
    >
      {children}
    </ResponsiveContainer>
  ) : (
    <div style={{ width: '100%', height: '100%', minHeight: minChart, minWidth: 0 }}>
      {children}
    </div>
  )

  return (
    <div
      ref={containerRef}
      className={`rg-chart ${animateIn ? 'rg-chart--enter' : ''} ${useFullHeight ? 'rg-chart--fill' : ''} ${className}`}
      style={useFullHeight ? { height: '100%', minHeight: 180 } : { height }}
    >
      {chartBody}
    </div>
  )
}

export default ChartContainer
